import React, { useState, useEffect, useRef } from "react";
import { fetchByJSON } from '../../util/util'
import { useSelector } from 'react-redux'
import { thebeConfigPython } from "../../util/thebeConfigs";
import { usePageKernel, attachCells } from "../../util/thebeSession";
import { Modal, Label } from '../../util/ui'
import { DropDown, Button } from '../../util/ui_components'
import './index.css'
import sanitizeJsonString from "../common/sanitize";

// const reconnectErrorFunction = () => {
//   alert('Kernel is taking too much time. Please reduce the data size!')
// }

const reconnectErrorFunction = () => {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 48px;
    height: 48px;
    border: 5px solid rgba(63, 91, 203, 0.84);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `;

  document.head.appendChild(style);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.remove();
    style.remove();
  }, 1000);
};

// Define initial code that creates df from the provided JSON data.
const InitialCodePython = (code) => `
import json
import pandas as pd
from io import StringIO
import numpy as np
MISSING_VALUES = ['-', '?', 'na', 'n/a', 'NA', 'N/A', 'nan', 'NAN', 'NaN', '*', '¶', '¶¶', '**']
data_io = StringIO(r"""${code}""")
try:
    df = pd.read_json(data_io)
except Exception as e:
    print("Error reading json data:", e)
`

// const InitialCodePython = (code) => `
// import json
// import pandas as pd
// from io import StringIO
// import numpy as np
// MISSING_VALUES = ['-', '?', 'na', 'n/a', 'NA', 'N/A', 'nan', 'NAN', 'NaN']
// data_io = StringIO(r"""${code}""")
// `

// Support code that can be used for download.
const supportCodePython = `
import pandas as pd
import numpy as np
import math
import matplotlib.pyplot as plt

# Manually import other libraries if necessary

# Replace <filename.csv> with the dataset you need
df = pd.read_csv('<filname.csv>')
`

// The default code to inspect df.
const defaultPythonCode = `
# Inspect the dataframe 'df'
print(type(df))

# Inspect the dataframe 'df'
print(df.columns)
print(df.info())
print(df.shape)
print(df.head())
`

// const defaultPythonCode = `
// # Inspect the dataframe 'df'
// print("hi")
// `

// Utility for escaping problematic characters in strings.
const stringEscape = (s) => {
  return s
    ? s
        .replace(/\\/g, '\\\\')
        .replace(/\n/g, '\\n')
        .replace(/\t/g, '\\t')
        .replace(/\v/g, '\\v')
        .replace(/'/g, "\\'")
        .replace(/"/g, '\\"')
        .replace(/[\x00-\x1F\x80-\x9F]/g, hex)
    : s;
  function hex(c) {
    var v = '0' + c.charCodeAt(0).toString(16);
    return '\\x' + v.substr(v.length - 2);
  }
}

const Page = () => {
  const [statusText, setStatusText] = useState('Loading...')
  const [selectedLanguage] = useState('python');
  const [code, setCode] = useState([])
  const dataset = useSelector(e => e.dataset)
  const kernelRef = useRef(null)
  // one kernel per mounted page, shut down again on unmount
  const getPageKernel = usePageKernel(thebeConfigPython)
  const isKernelReady = useRef(false)
  const mutated = useRef(1)
  const [downloadVarName, setDownloadVarName] = useState('')
  const [downloadFilename, setDownloadFilename] = useState('')
  const [showDatasetModal, setShowDatasetModal] = useState(false)

  // Initialize the kernel only once when the component mounts.
  useEffect(() => {
    reconnectErrorFunction();
    setStatusText('Loading...')
    reconnectErrorFunction();
    let isActive = true
    getPageKernel().then((kernel) => {
      if (!isActive) return
      // setStatusText('Waiting to run Initial Code')
      kernelRef.current = kernel
      kernelRef.current._reconnect = reconnectErrorFunction;
      isKernelReady.current = true
      // Normally a no-op: starting the kernel also rendered this page's
      // <pre data-executable>. Kept as the safety net for the case where the element
      // only reaches the DOM after the kernel is already up.
      attachCells(kernel)
      // Move the output area to the right part if available.
      const outputArea = document.querySelector('.jp-OutputArea');
      const rightPart = document.querySelector('#rightPart');
      if (outputArea && rightPart) {
        rightPart.appendChild(outputArea);
      }
      setStatusText('Ready')
    }).catch((err) => {
      if (!isActive) return
      console.error('Error connecting to the Python kernel:', err)
      setStatusText('Error connecting to Python')
    });

    return () => { isActive = false }
  }, []);

  // When the dataset changes, run initial code to define df.
  useEffect(() => {
    if (!dataset.filename) {
      setStatusText('No data');
      return;
    }
    // Wait until the kernel is ready.
    if (!isKernelReady.current) {
      setStatusText('Kernel Not Ready')
      return;
    };

    async function executeInitialCode() {
      try {
        setStatusText('Running Initial Code');
        const res = await fetchByJSON('current_data_json', {
          filename: dataset.filename
        });
        const g = await res.json();
        // Ensure you have a sanitizeJsonString function defined.
        const json_string = sanitizeJsonString(g.data);
        
        // await kernelRef.current.requestExecute({ code: InitialCodePython(json_string) }).done;
        setStatusText('Ready');
      } catch (error) {
        console.error("Error executing initial code:", error);
        setStatusText('Error loading dataframe');
      }
    }
    executeInitialCode();
  }, [dataset.filename]);

  const onDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([supportCodePython, ...code], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = selectedLanguage === "python" ? "download_test.py" : "download_test.r";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Downloads a Python dataframe (e.g. `df`, or anything else created in the session)
  // that only exists in the remote kernel's memory. df.to_csv('out.csv') alone would just
  // write the file on the remote Binder machine, which the user can never reach -- so
  // instead we ask the kernel to print the CSV text to stdout and capture it over the
  // IOPub stream, then hand it to the browser as a real client-side file download.
  const onDownloadDataset = async () => {
    if (!kernelRef.current) {
      alert('The Python kernel is not connected yet -- wait for status "Ready" and try again.')
      return
    }
    const varName = downloadVarName.trim()
    if (!varName) {
      alert('Enter the name of the Python variable/dataset you want to download (e.g. df).')
      return
    }

    try {
      setStatusText('Preparing dataset download ...')
      let csvText = ''
      let pyError = null
      let future = kernelRef.current.requestExecute({
        code: `print(${varName}.to_csv(index=False))`
      })
      future.onIOPub = (msg) => {
        if (msg.header.msg_type === 'stream' && msg.content.name === 'stdout') {
          csvText += msg.content.text
        } else if (msg.header.msg_type === 'error') {
          pyError = `${msg.content.ename}: ${msg.content.evalue}`
          console.error('Python error while exporting dataset:', msg.content)
        }
      }
      await future.done

      if (pyError) {
        prompt(`Python error while exporting "${varName}" (select all + copy):`, pyError)
        setStatusText('Ready')
        return
      }

      if (!csvText) {
        alert(`No data captured for "${varName}" -- make sure that object exists and is a dataframe in your current Python session (run your code first).`)
        setStatusText('Ready')
        return
      }

      const element = document.createElement('a')
      const file = new Blob([csvText], { type: 'text/csv' })
      element.href = URL.createObjectURL(file)
      element.download = (downloadFilename.trim() || `${varName}.csv`)
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
      setStatusText('Ready')
      setShowDatasetModal(false)
    } catch (err) {
      console.error('Error during dataset download:', err)
      setStatusText('Error during dataset download')
    }
  };

  return (
    <div className='flex flex-col h-full justify-between gap-2'>
      <div className="flex w-full justify-center items-center m-0 flex-grow-0 h-20">
        <div className="status-text">{statusText}</div>
      </div>
      <div className="flex justify-between items-center margin-left"></div>
      <div className="flex w-full h-full justify-between flex-grow-1 gap-2">
        <div className="code w-1/2 border-2 m-2 box-content flex flex-col">
          <div className="flex justify-between items-center bg-gray-400">
            <div
              className='run'
              onClick={async () => {
                if (!isKernelReady.current) {
                  console.warn("Kernel not ready");
                  return;
                }
                try {
                  setStatusText('Running the code ...');
                  // Reset mutation flag.
                  mutated.current = 0;
                  // Fetch and prepare data.
                  const res = await fetchByJSON('current_data_json', {
                    filename: dataset.filename
                  });
                  const json = await res.json();
                  const json_string = sanitizeJsonString(json.data);
                  const executeCode = selectedLanguage === 'python'
                    ? InitialCodePython(json.data)
                    : InitialCodeR(json.data);  // Ensure InitialCodeR is defined if needed.
                  // const executeCode = selectedLanguage === 'python'
                  //   ? InitialCodePython(json_string)
                  //   : InitialCodeR(json_string);  // Ensure InitialCodeR is defined if needed.
                  
                  // debugger;

                  let setupError = null;
                  let future = kernelRef.current.requestExecute({ code: executeCode });
                  future.onIOPub = (msg) => {
                    if (msg.header.msg_type === 'error') {
                      setupError = `${msg.content.ename}: ${msg.content.evalue}`;
                      console.error('Python error while setting up df:', msg.content);
                    }
                  };
                  await future.done;
                  if (setupError) {
                    prompt('Python error while preparing df (select all + copy):', setupError);
                    setStatusText('Error during run');
                    return;
                  }

                  // Trigger the run button in thebelab to execute the default code.
                  document.querySelector('.thebelab-run-button').click();
                  
                  // Capture the code from the left pane for download.
                  const appendCode = [];
                  document.querySelector('#leftPart')
                    .querySelectorAll('span[role="presentation"]')
                    .forEach((node) => {
                      appendCode.push('\n' + node.textContent);
                    });
                  setCode(appendCode);
                setStatusText('Ready');
                } catch (err) {
                  console.error("Error during run:", err);
                  setStatusText('Error during run');
                }
              }}
            >
              Run
            </div>
            <DropDown id="downloadOptionsPy" text="Download" width="w-48" zIndex={100} items={[
              { name: 'Download Code', onClick: onDownload },
              { name: 'Download Dataset', onClick: () => setShowDatasetModal(true) }
            ]} />
          </div>
          <pre data-executable="true" data-language={selectedLanguage} id="leftPart">
            {defaultPythonCode}
          </pre>
        </div>
        <div className="result w-1/2 border-2 m-2 box-content" id="rightPart"></div>
      </div>

      <Modal isOpen={showDatasetModal} setIsOpen={setShowDatasetModal} onClose={() => setShowDatasetModal(false)}>
        <div className="flex flex-col items-center justify-center py-10">
          <h1 className="text-3xl font-bold mb-6">Download Dataset</h1>
          <div className="w-full max-w-md bg-white p-6 rounded shadow-md">
            <Label text="Code to save dataframe to a file :" />
            <pre className="mb-4 bg-gray-100 p-4 rounded border border-gray-300 text-sm font-mono">
              {`${downloadVarName || '<dataset_name>'}.to_csv('${downloadFilename.trim() || (downloadVarName ? `${downloadVarName}.csv` : 'out.csv')}', index=False)`}
            </pre>
            <div className="mb-4">
              <Label text="Dataset variable name (e.g. df) :" />
              <input
                type="text"
                value={downloadVarName}
                onChange={(e) => setDownloadVarName(e.target.value)}
                placeholder="Enter the Python object name from your session"
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="mb-4">
              <Label text="Filename (optional) :" />
              <input
                type="text"
                value={downloadFilename}
                onChange={(e) => setDownloadFilename(e.target.value)}
                placeholder="Enter desired filename"
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex justify-end">
              <Button
                text="Download"
                onClick={onDownloadDataset}
                width="w-32"
                customStyle={{ padding: "0.5rem 1rem" }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Page;
